import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { asNumber } from "@cervval.js/common/src/tools/number.tools";
import { assertIsNever } from "@cervval.js/common/src/tools/object.tools";
import { FilterOptionsParams } from "@cervval.js/common/src/tools/string.tools";
import { RenderCacheData } from "@cervval.js/common/src/ui/renderCacheData.types";
import { anyReallyAny } from "@cervval.js/common/tools";
import { CompareOperators } from "./filterExpression.definition";

/** comparator for AND operation */
const COMPARATOR_AND = /[&;]/;
/** Comparator to put when comparator is not defined in the filter */
const EMPTY_COMPARATOR = "=";
/** Regex to detect [comparator(=,<,>,!...)][(+,-)number(10 power a number)] */
const REGEX_PARSE_FILTER = /^\s*([<>!=]*)\s*([-+]*[\d.,]+[eE]*[-+]?[\d.,]*)/;
/** If filter equals this string, return only empty (null or undefined) cells */
const EMPTY_CELL_FILTER_VALUE = "!";

/** List of comparators (AND) or EMPTY_CELL_FILTER_VALUE or null if filter is invalid */
type ParsedFilter = { op: CompareOperators, value: number }[] | typeof EMPTY_CELL_FILTER_VALUE | null;


/** compare a FilterOptionsParams for number, if filter is invalid return true to display all rows  */
export function filterComparatorNumber(p: FilterOptionsParams<anyReallyAny>, renderCacheData?: RenderCacheData): boolean {
    // -- Get filter ----------------------------------------------------------
    const key = p.filterValue;
    let filter = renderCacheData ? getProp<ParsedFilter>(renderCacheData, key) : undefined;
    if (filter == null) {
        filter = _parseFilter(p.filterValue);
        if (renderCacheData) {
            renderCacheData[key] = filter;
        }
    }

    // -- Check value against filter ------------------------------------------
    if (filter == null) {
        // Invalid filter, always show result, this is safest for the user.
        // He will easily understand that the filter is not applied.
        return true;
    } else if (filter === EMPTY_CELL_FILTER_VALUE) {
        return p.itemValue == null;
    } else if (filter.length === 0) {
        // Quick exit for empty filter
        return true;
    }

    // -------- Split filters ---------------------------------------
    if (p.itemValue == null) {
        // Invalid number, we cannot keep it
        return false;
    }
    const value = typeof p.itemValue === "number" ? p.itemValue : asNumber(p.itemValue.replace(",", ".")) ?? undefined;
    if (value == null) {
        // Invalid number, we cannot keep it
        return false;
    }
    for (const expression of filter) {
        if (!compareNumberValue(value, expression.op, expression.value)) {
            // Exit on first expression not matched
            return false;
        }
    }
    return true;
}

/** return true if a number value match the numerical compare value by the operator */
function compareNumberValue(value1: number, operator: CompareOperators, value2: number): boolean {
    switch (operator) {
        case "=":
            return Math.abs(value1 - value2) < Number.EPSILON;
        case "<>":
            return !(Math.abs(value1 - value2) < Number.EPSILON);
        case "<":
            return value1 < value2;
        case "<=":
            return value1 <= value2;
        case ">":
            return value1 > value2;
        case ">=":
            return value1 >= value2;
        default:
            assertIsNever(operator);
    }
    return false;
}

//#region Filter / value parsing

/** Parse a filter input by the user */
function _parseFilter(filterStr: string): ParsedFilter {
    const filterTrimmed = filterStr.trim();
    if (filterTrimmed.trim() === "") {
        // Quick exit empty filters
        return [];
    } else if (filterTrimmed === EMPTY_CELL_FILTER_VALUE) {
        return EMPTY_CELL_FILTER_VALUE;
    }

    const comparators: ParsedFilter = [];
    const filterValues = filterStr.split(COMPARATOR_AND);
    for (const filterValue of filterValues) {
        // We are obliged to check all the comparisons, to display all the lines if a comparator is incorrectly defined
        // ---------------- Parse filter ----------------------------
        // No need to trim, regex will only return non-whitespace components
        const result = filterValue.match(REGEX_PARSE_FILTER);
        if (!result || result.length !== 3) {
            // Invalid filter
            return null;
        }
        // Parse both parts of the string returned by the regex
        const comparator: CompareOperators | undefined = _getCompareOperators(result[1], EMPTY_COMPARATOR);
        const refValue = asNumber(result[2].replace(",", ".")); // Allow user to input ","

        if (comparator == null || refValue == null) {
            // If filter invalid, show all rows
            return null;
        }
        // --------- Check that value matches the filter -------------
        comparators.push({ op: comparator, value: refValue });
    }
    return comparators;
}

/** get comparator from input, get comparatorIfEmpty if doesn't have comparator in this input  */
function _getCompareOperators(input: string, comparatorIfEmpty?: CompareOperators): CompareOperators | undefined {
    switch (input) {
        // ------------ Equal ---------------
        case "=":
        case "==":
        case "===":
            return "=";
        // ------------ Not equal -----------
        case "!":
        case "!=":
        case "<>":
            return "<>";
        // ------------ Less -----------------
        case "<":
            return "<";
        // ---------- Less or equal ----------
        case "<=":
        case "=<":
            return "<=";
        // ---------- Greater ----------------
        case ">":
            return ">";
        // ------- Greater or equal ----------
        case ">=":
        case "=>":
            return ">=";
        case "":
            return comparatorIfEmpty ?? undefined;
        default:
            return undefined;
    }
}

//#endregion